import type { Node } from '../../../../core/models/scene';
import type { TableSchema } from './tableSchema.types';

const MIN_CELL = 20;

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const ensurePositive = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  return fallback;
};

const parseCellKey = (key: string): { r: number; c: number } | null => {
  const [rStr, cStr] = key.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};

interface TableConversionResult {
  width: number;
  height: number;
  attrs: Record<string, unknown>;
}

export const mapTableNodeToSchema = (node: Node, schema: TableSchema): TableConversionResult => {
  const baseWidth = ensurePositive((node as any).width ?? (node as any).w, 240);
  const baseHeight = ensurePositive((node as any).height ?? (node as any).h, 180);

  const rowCount = clampCount(schema.grid.rows, 3);
  const columnCount = clampCount(schema.grid.cols, 3);

  const rowsConfig = schema.rows ?? [];
  const colsConfig = schema.cols ?? [];

  const headerRowIndices = rowsConfig.filter((r) => r?.isHeader).map((r) => r.index);
  const headerColIndices = colsConfig.filter((c) => c?.isHeader).map((c) => c.index);
  const showHeader = headerRowIndices.includes(0);
  const showHeaderColumn = headerColIndices.includes(0);

  const headerRow0 = rowsConfig.find((r) => r.index === 0 && r.isHeader);
  const headerHeightDefined =
    typeof headerRow0?.height === 'number' && Number.isFinite(headerRow0.height) ? headerRow0.height : undefined;

  const headerHeight = showHeader ? ensurePositive(headerHeightDefined, 32) : 0;

  const defaultRowHeight = schema.dimensions?.defaultRowHeight;
  const defaultColWidth = schema.dimensions?.defaultColWidth;
  const minCellHeight =
    typeof schema.dimensions?.minCellHeight === 'number' && Number.isFinite(schema.dimensions.minCellHeight)
      ? Math.max(MIN_CELL, schema.dimensions.minCellHeight)
      : MIN_CELL;
  const minCellWidth =
    typeof schema.dimensions?.minCellWidth === 'number' && Number.isFinite(schema.dimensions.minCellWidth)
      ? Math.max(MIN_CELL, schema.dimensions.minCellWidth)
      : MIN_CELL;

  const width =
    typeof defaultColWidth === 'number' && Number.isFinite(defaultColWidth)
      ? Math.max(columnCount * minCellWidth, defaultColWidth * columnCount)
      : baseWidth;

  const bodyTargetHeight =
    typeof defaultRowHeight === 'number' && Number.isFinite(defaultRowHeight)
      ? Math.max(rowCount * minCellHeight, defaultRowHeight * rowCount)
      : Math.max(MIN_CELL * rowCount, baseHeight - (showHeader ? headerHeight : 0));

  const height = bodyTargetHeight + (showHeader ? headerHeight : 0);

  const rowHeights =
    typeof defaultRowHeight === 'number' && Number.isFinite(defaultRowHeight)
      ? Array(rowCount).fill(Math.max(minCellHeight, defaultRowHeight))
      : Array(rowCount).fill(Math.max(minCellHeight, bodyTargetHeight / Math.max(1, rowCount)));

  const columnWidths =
    typeof defaultColWidth === 'number' && Number.isFinite(defaultColWidth)
      ? Array(columnCount).fill(Math.max(minCellWidth, defaultColWidth))
      : Array(columnCount).fill(Math.max(minCellWidth, width / Math.max(1, columnCount)));

  const existingAttrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
  const totalRenderRows = showHeader ? rowCount + 1 : rowCount;

  const preserveCellMap = <T extends Record<string, unknown>>(src: unknown): T => {
    if (!src || typeof src !== 'object') return {} as T;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(src as Record<string, unknown>)) {
      const parsed = parseCellKey(key);
      if (!parsed) continue;
      if (parsed.r < 0 || parsed.c < 0) continue;
      if (parsed.r >= totalRenderRows) continue;
      if (parsed.c >= columnCount) continue;
      out[key] = value;
    }
    return out as T;
  };

  const cellText = preserveCellMap<Record<string, string>>((existingAttrs as any).cellText);
  const cellStyles = preserveCellMap<Record<string, unknown>>((existingAttrs as any).cellStyles);
  const cellLayouts = preserveCellMap<Record<string, unknown>>((existingAttrs as any).cellLayouts);

  const headerFillColor = schema.theme?.headerBackground;
  const gridColor = schema.theme?.borderColor;
  const strokeWidth = schema.theme?.borderWidth;

  const attrs: Record<string, unknown> = {
    ...existingAttrs,
    rowCount,
    columnCount,
    rowHeights,
    columnWidths,
    showHeader,
    showHeaderColumn,
    headerRowIndices,
    headerColIndices,
    ...(showHeader ? { headerHeight } : {}),
    ...(typeof headerFillColor === 'string' ? { headerFillColor } : {}),
    ...(typeof gridColor === 'string' ? { gridColor } : {}),
    ...(typeof strokeWidth === 'number' && Number.isFinite(strokeWidth) ? { strokeWidth } : {}),
    ...(Object.keys(cellText).length > 0 ? { cellText } : {}),
    ...(Object.keys(cellStyles).length > 0 ? { cellStyles } : {}),
    ...(Object.keys(cellLayouts).length > 0 ? { cellLayouts } : {}),
    mergedRegions: schema.mergedRegions ?? [],
    schemaId: schema.id,
  };

  return {
    width,
    height,
    attrs,
  };
};

